import type { Hold } from '../../types/climb';
import type { BoardHand } from '../../types/device';
import { assignCircuitHands, circuitHandsOf, clearCircuitHands, swapCircuitHand } from './circuit';
import { photoHoldRole, type PhotoHoldRole } from './photo-hold-roles';

/**
 * A climb circled on a photo, read as a **sequence** instead of a set.
 *
 * The board has had this for a while — see `board-grid/roles.ts`, whose rules
 * these are — and nothing about it was ever about the grid: a circuit is the
 * order the holds are climbed in, and a wall photographed by the setter holds an
 * order just as well as a lattice of LEDs does. So the rules are the same ones,
 * restated against the one thing that differs: a photo hold says what it is by
 * its **colour** (`Hold.color`, see photo-hold-roles) rather than by a `role`
 * field.
 *
 * The order is the holds array's own order. Nothing numbers a hold: the number
 * is where it sits, which is why a reorder renumbers everything by itself and
 * why neither the client nor the server needs a column for it (JSONB keeps the
 * order it was given).
 */

/**
 * Where each role sits in the climbed order: a climb is climbed from its start
 * holds to its top, so those bracket everything between them. Kept in sync with
 * BoardClimb::HOLD_ROLE_ORDER and `board-grid/roles.ts`.
 */
const ROLE_ORDER: Record<PhotoHoldRole, number> = {
  start: 0,
  hand: 1,
  foot: 1,
  no_match: 1,
  match: 1,
  finish: 2,
};

function roleRank(hold: Hold): number {
  return ROLE_ORDER[photoHoldRole(hold)];
}

/**
 * Start holds first, tops last, everything else exactly where the setter put it.
 *
 * The sort is **stable**, so reordering the middle of a circuit still means
 * something while the two ends stay where a climb's ends belong — which is what
 * lets the setter drag freely instead of being refused.
 */
export function orderPhotoHolds(holds: Hold[]): Hold[] {
  return [...holds]
    .map((hold, index) => ({ hold, index }))
    .sort((a, b) => roleRank(a.hold) - roleRank(b.hold) || a.index - b.index)
    .map(({ hold }) => hold);
}

/** A hold the climber stands on rather than moves to — no step of the circuit. */
export function isPhotoFoot(hold: Hold): boolean {
  return photoHoldRole(hold) === 'foot';
}

/**
 * A hold both hands come onto. It is **one circle and two moves**: the climber
 * reaches it with one hand and brings the other onto it, so it takes two of the
 * circuit's numbers and two of its hands.
 *
 * The board says the same thing by using a cell twice over (see BoardClimb); a
 * photo cannot, because a circle *is* the hold, so here it is a role.
 */
export function isPhotoMatch(hold: Hold): boolean {
  return photoHoldRole(hold) === 'match';
}

/** How many moves of the circuit this one circle is: a match is two, a foot none. */
export function photoStepCount(hold: Hold): number {
  if (isPhotoFoot(hold)) return 0;

  return isPhotoMatch(hold) ? 2 : 1;
}

/**
 * The run itself, as indexes into the drawing: every circle but the feet, in the
 * order they are climbed.
 *
 * Indexes rather than the holds, because a place in the run is what the editor
 * works in — a circle is pointed at, moved and dropped by where it sits.
 */
export function photoStepIndexes(holds: Hold[]): number[] {
  return holds.reduce<number[]>((steps, hold, index) => {
    if (!isPhotoFoot(hold)) steps.push(index);
    return steps;
  }, []);
}

/** The other half of the drawing: the feet, in the same terms. */
export function photoFootIndexes(holds: Hold[]): number[] {
  return holds.reduce<number[]>((feet, hold, index) => {
    if (isPhotoFoot(hold)) feet.push(index);
    return feet;
  }, []);
}

/**
 * The number each circle wears, aligned with the holds themselves — null for a
 * foot, which the run passes over rather than stopping on, so the circle after a
 * foot takes the next number and not the one after it.
 *
 * A match wears both of the numbers it takes ("3,4"): one circle, two moves, and
 * the circle after it carries on from the second of them.
 */
export function photoHoldNumbers(holds: Hold[]): (string | null)[] {
  let step = 0;

  return holds.map((hold) => {
    const moves = photoStepCount(hold);
    if (moves === 0) return null;

    const first = ++step;
    if (moves === 1) return String(first);

    return `${first},${++step}`;
  });
}

/**
 * The first circle the setter's chosen hand belongs to: the first one that is
 * neither a start nor a foot.
 *
 * A start is pulled on with **both** hands — that is what a start position is,
 * and a sit start marks the feet it is pushed off as well — so there is no one
 * hand to name there. The alternation begins on the first real move, which is
 * also the one hold the setter is asked about.
 */
export function forcedHandIndex(holds: Hold[]): number | null {
  const at = holds.findIndex((hold) => takesAHand(hold));

  return at === -1 ? null : at;
}

/** Whether this circle is one a single hand is named for. */
function takesAHand(hold: Hold): boolean {
  const role = photoHoldRole(hold);

  return role !== 'start' && role !== 'foot';
}

/**
 * Which hand takes each hold of a circuit that forces them, given the hand the
 * first real move is made with — the shared rule (see `circuit.ts`), read off
 * the circles' colours. The twin of Boulder#assign_photo_hands.
 */
export function assignPhotoHands(holds: Hold[], startingHand: BoardHand): Hold[] {
  return assignCircuitHands(holds, photoHoldRole, startingHand);
}

/**
 * Swap the hand this circle is taken with, and let the run alternate on from
 * it — see `circuit.ts#swapCircuitHand`.
 */
export function swapPhotoHand(holds: Hold[], index: number): Hold[] {
  return swapCircuitHand(holds, photoHoldRole, index);
}

/**
 * Both hands a match is taken with, in order — the one that arrives and the one
 * that joins it. One entry for every other hold.
 */
export function photoHandsOf(hold: Hold): BoardHand[] {
  return circuitHandsOf(hold, photoHoldRole);
}

/** Strips the hands off a drawing, for a climb that stops forcing them. */
export function clearPhotoHands(holds: Hold[]): Hold[] {
  return clearCircuitHands(holds);
}

/**
 * The hand a circuit's first move is made with, read back off its holds — the
 * first one that names a hand, every save having rewritten the run from it.
 * Falls back to the left hand, as the server does.
 *
 * Only the holds a hand is actually named for are asked: a start carrying one is
 * a leftover rather than an answer, and letting it seed the alternation would
 * flip a whole circuit over a hand nobody chose (see Boulder#starting_hand).
 */
export function startingPhotoHand(holds: Hold[]): BoardHand {
  return holds.find((hold) => hold.hand && takesAHand(hold))?.hand ?? 'left';
}

/**
 * Whether this circle can take the place of the one before (or after) it in the
 * run.
 *
 * Only among equals: a hand hold cannot be dragged in front of a start or past
 * the top, so the button that would do it is dead rather than pressed to no
 * effect. Feet are not in the run at all and so never move.
 */
export function canMovePhotoStep(holds: Hold[], index: number, delta: -1 | 1): boolean {
  return photoStepNeighbour(holds, index, delta) !== null;
}

/**
 * Swap this circle with the one before or after it in the run, leaving the feet
 * between them where they are — they are no step of it.
 *
 * Returns the holds unchanged where the move is not allowed, so the caller can
 * hand the result straight on.
 */
export function movePhotoStep(holds: Hold[], index: number, delta: -1 | 1): Hold[] {
  const neighbour = photoStepNeighbour(holds, index, delta);
  if (neighbour === null) return holds;

  const moved = [...holds];
  [moved[index], moved[neighbour]] = [moved[neighbour], moved[index]];
  return moved;
}

/**
 * Where the circle this one would trade places with sits, or null when there is
 * none it may: off the end of the run, or one of the two ends of the climb,
 * which a hold climbed through must not take the place of.
 */
export function photoStepNeighbour(holds: Hold[], index: number, delta: -1 | 1): number | null {
  const hold = holds[index];
  if (!hold || isPhotoFoot(hold)) return null;

  const steps = photoStepIndexes(holds);
  const at = steps.indexOf(index);
  const neighbour = steps[at + delta];
  if (at === -1 || neighbour === undefined) return null;

  return roleRank(holds[neighbour]) === roleRank(hold) ? neighbour : null;
}

/**
 * The drawing with one circle dragged onto another's place — the list's own
 * gesture, where the button above moves one step at a time.
 *
 * Dropped wherever the setter let go and then settled: a climb is climbed from
 * its starts to its top, so the middle reorders freely and the two ends go back
 * where they belong instead of the drag being refused.
 */
export function dragPhotoHold(holds: Hold[], from: number, to: number): Hold[] {
  if (!holds[from] || !holds[to] || from === to) return holds;

  const moved = [...holds];
  moved.splice(to, 0, ...moved.splice(from, 1));
  return orderPhotoHolds(moved);
}
